import { defineStore } from 'pinia';
import SharedService from '@/v2/services/shared';
import TemplatesEntity from '@/v2/modules/whatsapp/entities/templates';
import { Alert } from '@/v2/plugins/alert';
import { TemplateTypes } from '@/v2/modules/whatsapp/entities/templates/interfaces.js';
import { t } from '@/locales/index';
import { featureFlag } from '@/v2/entities/feature-flag';

export const useTemplatesStore = defineStore('templatesStore', {
	state: () => ({
		configs: null,
		loading: false,
		loadingConfig: false,
		loadingPage: false,
		isEditable: false,
		id: null,
		initialVariableIndices: undefined,
		initialForm: null,
		currentForm: null,
	}),
	getters: {
		templatePreview(state) {
			return {
				template: {
					id: state.id,
				},
				category: state.currentForm?.category,
				header: {
					format: state.currentForm?.header?.format,
					text: state.currentForm?.header?.text,
				},
				body: {
					text: state.currentForm?.body?.text,
					example: state.currentForm?.body?.example,
				},
				footer: {
					text: state.currentForm?.footer?.text,
				},
				buttons: {
					buttons: state.currentForm?.buttons?.buttons,
				},
				lto: {
					text: state.currentForm?.limited_time_offer?.limited_time_offer?.text,
				},
			};
		},
	},
	actions: {
		async fetchConfigs() {
			this.loadingConfig = true;
			try {
				const resp = await SharedService.getEnumsList(['WhatsappTemplateEnum']);
				this.configs = resp?.WhatsappTemplateEnum;

				this.configs?.types?.map((type) => {
					if ([TemplateTypes.MULTI_PRODUCT, TemplateTypes.CATALOG].includes(type.type)) {
						type.featureFlag = featureFlag.E_COMMERCE;
					}
				});
			} finally {
				this.loadingConfig = false;
			}
		},
		async getTemplate(id) {
			this.loadingPage = true;

			try {
				try {
					const resp = await TemplatesEntity.getTemplate(id);
					const template = resp?.data?.result?.whatsapp_template;

					const components = template.components.filter((el) => !!el);

					const headerComponent = components.find((el) => el.type === 'HEADER');
					const bodyComponent = components.find((el) => el.type === 'BODY');
					const footerComponent = components.find((el) => el.type === 'FOOTER');
					const buttonsComponent = components.find((el) => el.type === 'BUTTONS');
					const ltoComponent = components.find((el) => el.type === 'LIMITED_TIME_OFFER');

					const form = {
						name: template.name,
						language: template.language,
						category: template.category,
						type: template.type,
						header: headerComponent
							? {
									format: headerComponent?.format,
									text: headerComponent?.text,
									type: headerComponent?.type,
							  }
							: undefined,
						body: {
							text: bodyComponent?.text,
							example: bodyComponent?.example,
							type: bodyComponent?.type,
						},
						footer: {
							text: footerComponent?.text,
							type: footerComponent?.type,
						},
						buttons: {
							buttons: buttonsComponent?.buttons.map((btn) => ({ ...btn, toggle: true })),
							type: buttonsComponent?.type,
						},
					};
					if (ltoComponent) {
						form.limited_time_offer = {
							type: 'LIMITED_TIME_OFFER',
							limited_time_offer: {
								text: ltoComponent?.text,
								has_expiration: ltoComponent?.has_expiration,
							},
						};
					}

					this.initialForm = form;

					const variables = form?.body?.example?.body_text?.length
						? form.body?.example?.body_text[0]
						: [];
					const variablesIndices = {};
					variables.forEach((variable, i) => {
						variablesIndices[variable] = i + 1;
					});
					this.initialVariableIndices = variablesIndices;
				} catch (error) {
					console.error(error);
				} finally {
					this.loadingPage = false;
				}
			} catch (error) {
				console.error(error);
				this.loadingPage = false;
			}
		},
		async createTemplate({ data }) {
			this.loading = true;
			try {
				await TemplatesEntity.createTemplate(data);
				Alert.success(t('createdSuccessfully'));
				return true;
			} catch (error) {
				Alert.error(error?.response?.data?.message);
			} finally {
				this.loading = false;
			}
		},
		async updateTemplate({ id, data }) {
			this.loading = true;
			try {
				await TemplatesEntity.updateTemplate(id, data);
				Alert.success(t('updatedSuccessfully'));
				return true;
			} catch (error) {
				Alert.error(error?.response?.data?.message);
			} finally {
				this.loading = false;
			}
		},
		resetData() {
			this.initialForm = null;
			this.currentForm = null;
			this.initialVariableIndices = undefined;
			this.id = null;
			this.isEditable = false;
			this.loading = false;
			this.loadingConfig = false;
			this.loadingPage = false;
		},
	},
});
